interface PerformanceRadarProps {
    scores: {
        technical: number;
        compliance: number;
        pastPerformance: number;
        financial: number;
    };
}

export function PerformanceRadar({ scores }: PerformanceRadarProps) {
    const size = 200;
    const center = size / 2;
    const radius = size * 0.4;

    const categories = [
        { key: 'technical', label: 'Technical' },
        { key: 'compliance', label: 'Compliance' },
        { key: 'pastPerformance', label: 'Past Perf' },
        { key: 'financial', label: 'Financial' }
    ];

    const points = categories.map((cat, i) => {
        const score = (scores as any)[cat.key] || 0;
        const angle = (Math.PI * 2 * i) / categories.length - Math.PI / 2;
        const r = (score / 100) * radius;
        return {
            x: center + r * Math.cos(angle),
            y: center + r * Math.sin(angle),
            labelX: center + (radius + 20) * Math.cos(angle),
            labelY: center + (radius + 20) * Math.sin(angle)
        };
    });

    const pathData = `M ${points[0].x} ${points[0].y} ` + points.slice(1).map(p => `L ${p.x} ${p.y}`).join(' ') + ' Z';

    return (
        <div className="relative flex flex-col items-center">
            <svg width={size + 60} height={size + 60} viewBox={`0 0 ${size + 60} ${size + 60}`} className="overflow-visible">
                {/* Background Circles */}
                {[0.2, 0.4, 0.6, 0.8, 1].map((step) => (
                    <circle
                        key={step}
                        cx={center + 30}
                        cy={center + 30}
                        r={radius * step}
                        fill="none"
                        stroke="white"
                        strokeOpacity="0.05"
                        strokeDasharray="2,2"
                    />
                ))}

                {/* Axes */}
                {points.map((p, i) => (
                    <line
                        key={i}
                        x1={center + 30}
                        y1={center + 30}
                        x2={center + 30 + (p.labelX - center)}
                        y2={center + 30 + (p.labelY - center)}
                        stroke="white"
                        strokeOpacity="0.1"
                    />
                ))}

                {/* Labels */}
                {categories.map((cat, i) => (
                    <text
                        key={i}
                        x={center + 30 + (points[i].labelX - center)}
                        y={center + 30 + (points[i].labelY - center)}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        className="text-[10px] font-bold fill-gray-500 uppercase tracking-tighter"
                    >
                        {cat.label}
                    </text>
                ))}

                {/* Data Polygon */}
                <path
                    d={pathData.split(' ').map((val) => {
                        if (isNaN(Number(val))) return val;
                        return Number(val) + 30;
                    }).join(' ')}
                    fill="url(#radarGradient)"
                    fillOpacity="0.3"
                    stroke="#3B82F6"
                    strokeWidth="2"
                    className="drop-shadow-[0_0_8px_rgba(59,130,246,0.5)]"
                />

                <defs>
                    <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#3B82F6" />
                        <stop offset="100%" stopColor="#1D4ED8" />
                    </linearGradient>
                </defs>

                {/* Data Points */}
                {points.map((p, index) => {
                    return (
                        <circle
                            key={index}
                            cx={p.x + 30}
                            cy={p.y + 30}
                            r="3"
                            fill="#3B82F6"
                        />
                    );
                })}
            </svg>
        </div>
    );
}
